import * as React from 'react';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  DeleteButton,
  TextInput,
  NumberField,
  NumberInput,
  required,
  BooleanInput,
  ImageInput,
  ImageField,
  FormDataConsumer,
  useTranslate,
  useLocaleState,
  useEditContext,
} from 'react-admin';

import EmptyList from '../components/misc/EmptyList';
import HomeSectionCategories from '../components/form/HomeSectionCategories';
import HomeSectionSeeAll from '../components/form/HomeSectionSeeAll';
import HomeSectionManualEvents from '../components/form/HomeSectionManualEvents';
import HomeSectionCarouselDesign from '../components/form/HomeSectionCarouselDesign';
import HomeSectionMarquee from '../components/form/HomeSectionMarquee';
import HomeSectionVenue from '../components/form/HomeSectionVenue';
import SourceLanguageBanner from '../components/form/SourceLanguageBanner';
import { useSourceLanguage } from '../hooks/useSourceLanguage';

const entity = 'homeSections';

export const HomeSectionList = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeTitleKey = locale === 'tr' ? 'title.tr' : 'title.en';

  return (
    <List
      {...props}
      title={translate('homeSections.title', { _: 'Ana Sayfa Bölümleri' })}
      empty={<EmptyList entity={entity} />}
      sort={{ field: 'order', order: 'ASC' }}
    >
      <Datagrid>
        <TextField source={localeTitleKey} label={translate('news.news_title', { _: 'Başlık' })} />
        <NumberField source="order" label={translate('homeSections.order', { _: 'Sıra' })} />
        <EditButton />
        <DeleteButton basePath={`/${entity}`} />
      </Datagrid>
    </List>
  );
};

const HomeSectionFormFields = ({ isCreate = false, isEdit = false, record }) => {
  const translate = useTranslate();
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <h3>{translate('homeSections.main_details', { _: 'Temel Bilgiler' })}</h3>
      <NumberInput
        source="order"
        label={translate('homeSections.order', { _: 'Sıra' })}
        validate={required()}
        fullWidth
      />
      <TextInput
        source={localizedSource('title')}
        label={translate('homeSections.section_title', { _: 'Başlık' })}
        validate={required()}
        fullWidth
      />
      <ImageInput
        source="banner"
        label={translate('venues.banner', { _: 'Kapak Görseli' })}
        accept="image/*"
        defaultValue={null}
      >
        <ImageField source="src" title="title" />
      </ImageInput>

      <BooleanInput
        label={translate('homeSections.is_featured', {
          _: 'Öne Çıkanlar bölüm modeli?',
        })}
        source="isFeatured"
        defaultValue={isCreate ? false : undefined}
      />
      <FormDataConsumer>
        {({ formData }) => {
          if (!formData?.isFeatured) return null;
          return (
            <span
              style={{
                fontSize: 12,
                display: 'block',
                marginBottom: 16,
                color: '#555',
              }}
            >
              {translate('homeSections.featured_help', {
                _: 'Öne Çıkanlar: kart butonu "Satın Al" yerine aktif ürünlerin en düşük fiyatını gösterir.',
              })}
            </span>
          );
        }}
      </FormDataConsumer>

      <HomeSectionVenue isCreate={isCreate} />

      <FormDataConsumer>
        {({ formData }) => {
          if (formData?.useVenue) return null;
          return <HomeSectionCategories isCreate={isCreate} />;
        }}
      </FormDataConsumer>

      <h3>{translate('homeSections.content', { _: 'İçerik' })}</h3>
      <FormDataConsumer>
        {({ formData }) => {
          if (formData?.useVenue) {
            return (
              <BooleanInput
                label={translate('homeSections.fetch_upcoming_venue', {
                  _: 'Bu mekanın yaklaşan etkinliklerini getir?',
                })}
                source="fetchUpcoming"
                defaultValue={isCreate ? true : undefined}
              />
            );
          }
          return (
            <BooleanInput
              label={translate('homeSections.fetch_upcoming_category', {
                _: 'Kategorinin yaklaşan etkinliklerini göster?',
              })}
              source="fetchUpcoming"
              defaultValue={isCreate ? true : undefined}
            />
          );
        }}
      </FormDataConsumer>

      <HomeSectionSeeAll isCreate={isCreate} localizedSource={localizedSource} />
      <HomeSectionManualEvents isCreate={isCreate} />
      <HomeSectionMarquee isCreate={isCreate} />
      <HomeSectionCarouselDesign isCreate={isCreate} />
    </React.Fragment>
  );
};

const HomeSectionEditForm = () => {
  const { record } = useEditContext();
  return (
    <SimpleForm redirect={`/${entity}`}>
      <HomeSectionFormFields isEdit record={record} />
    </SimpleForm>
  );
};

export const HomeSectionEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit
      title={translate('homeSections.edit_section', {
        _: 'Ana Sayfa Bölümünü Düzenle',
      })}
      {...props}
      transform={(data, { previousData }) => ({
        ...data,
        sourceLang: previousData?.sourceLanguage || 'tr',
      })}
    >
      <HomeSectionEditForm />
    </Edit>
  );
};

export const HomeSectionCreate = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  return (
    <Create
      title={translate('homeSections.new_section', {
        _: 'Yeni Ana Sayfa Bölümü',
      })}
      {...props}
      redirect={'list'}
      transform={(data) => ({
        ...data,
        sourceLang: locale || 'tr',
      })}
    >
      <SimpleForm redirect={`/${entity}`}>
        <HomeSectionFormFields isCreate />
      </SimpleForm>
    </Create>
  );
};
